/**
 * Configuracion del menu lateral (sidebar).
 *
 * Cada item declara:
 * - key: identificador unico
 * - label: texto visible
 * - icon: nombre del Material Symbol
 * - path: ruta absoluta dentro de la app
 * - permissionCode (opcional): si se setea, el item se filtra por permisos del usuario
 * - module: agrupacion para el header de seccion
 * - children: sub-items (submenu colapsable). Si esta presente, el item actua como "padre".
 *
 * Reglas:
 * - Para que un item renderice hijos, debe existir `children` con al menos un sub-item.
 * - Los modulos sin items visibles (por permisos) NO se renderizan.
 * - Los modulos con sub-items inician colapsados; el que contiene la ruta activa se auto-expande.
 */
export type MenuItem = {
  key: string;
  label: string;
  icon: string;
  path: string;
  permissionCode?: string;
  permissionAnyCodes?: readonly string[];
  module?: string;
  children?: MenuItem[];
};

/** Aplica los permisos declarados por un acceso del menú, también a sus hijos. */
export function canAccessMenuItem(item: MenuItem, hasPermission: (code: string) => boolean): boolean {
  return (!item.permissionCode || hasPermission(item.permissionCode))
    && (!item.permissionAnyCodes || item.permissionAnyCodes.some(hasPermission));
}

export const menuSections: { module: string; title: string; items: MenuItem[] }[] = [
  {
    module: 'general',
    title: 'General',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: 'space_dashboard', path: '/app/dashboard', permissionCode: 'dashboard.view', module: 'general' },
    ],
  },
  {
    module: 'commercial',
    title: 'Comercial',
    items: [
      { key: 'quote-generator', label: 'Cotizador', icon: 'point_of_sale', path: '/app/quote-generator/new', permissionCode: 'quotations.create', module: 'commercial' },
      { key: 'clients', label: 'Clientes', icon: 'contacts', path: '/app/clients', permissionCode: 'clients.view', module: 'commercial' },
      { key: 'customer-requirements', label: 'Requerimientos', icon: 'assignment', path: '/app/customer-requirements', permissionCode: 'customer_requirements.view', module: 'commercial' },
      { key: 'budgets', label: 'Presupuestos', icon: 'calculate', path: '/app/budgets', permissionCode: 'budgets.view', module: 'commercial' },
      { key: 'quotations', label: 'Cotizaciones', icon: 'request_quote', path: '/app/quotations', permissionCode: 'quotations.view', module: 'commercial' },
      { key: 'sales-items', label: 'Articulos de Venta', icon: 'sell', path: '/app/sales-items', permissionCode: 'master_items.view', module: 'commercial' },
      { key: 'reports', label: 'Reportes', icon: 'bar_chart', path: '/app/reports', permissionCode: 'reports.view', module: 'commercial' },
    ],
  },
  {
    module: 'catalog',
    title: 'Maestros',
    items: [
      { key: 'suppliers', label: 'Proveedores', icon: 'local_shipping', path: '/app/suppliers', permissionCode: 'suppliers.view', module: 'catalog' },
      { key: 'master-item-categories', label: 'Categorias', icon: 'category', path: '/app/master-item-categories', permissionCode: 'master_items.view', module: 'catalog' },
      { key: 'resources', label: 'Recursos', icon: 'inventory_2', path: '/app/resources', permissionCode: 'master_items.view', module: 'catalog' },
      { key: 'components', label: 'Partidas', icon: 'account_tree', path: '/app/components', permissionCode: 'master_items.view', module: 'catalog' },
      { key: 'models', label: 'Modelos', icon: 'dashboard_customize', path: '/app/models', permissionCode: 'master_items.view', module: 'catalog' },
      { key: 'units', label: 'Unidades', icon: 'straighten', path: '/app/units', permissionCode: 'units.view', module: 'catalog' },
      { key: 'currencies', label: 'Monedas', icon: 'payments', path: '/app/currencies', permissionCode: 'currencies.view', module: 'catalog' },
      { key: 'data-import', label: 'Carga masiva', icon: 'upload_file', path: '/app/data-import', permissionCode: 'data_import.view', module: 'catalog' },
    ],
  },
  {
    module: 'workflow',
    title: 'Workflow',
    items: [
      { key: 'workflow-tasks', label: 'Mis tareas', icon: 'task_alt', path: '/app/workflow-tasks', permissionCode: 'workflow_tasks.view_own', module: 'workflow' },
      { key: 'workflow-instances', label: 'Seguimiento', icon: 'account_tree', path: '/app/workflow-instances', permissionCode: 'workflow_instances.view', module: 'workflow' },
      { key: 'workflow-operations', label: 'Operación de workflows', icon: 'monitor_heart', path: '/app/workflow-operations', permissionCode: 'workflow_instances.manage', module: 'workflow' },
    ],
  },
  {
    module: 'workflow-config',
    title: 'Workflow · Configuración',
    items: [
      { key: 'workflows', label: 'Definir Workflow', icon: 'schema', path: '/app/workflows', permissionCode: 'workflow_definitions.view', module: 'workflow-config' },
      { key: 'workflow-actions', label: 'Recorridos por Tenant', icon: 'route', path: '/app/workflow-actions', permissionAnyCodes: ['workflow_actions.view', 'workflow_routes.view'], module: 'workflow-config' },
      { key: 'platform-workflow-actions', label: 'Recorridos de Plataforma', icon: 'bolt', path: '/app/platform-workflow-actions', permissionAnyCodes: ['platform.workflow_actions.view', 'platform.workflow_routes.view'], module: 'workflow-config' },
    ],
  },
  {
    module: 'admin',
    title: 'Administracion',
    items: [
      { key: 'users', label: 'Usuarios', icon: 'group', path: '/app/users', permissionCode: 'users.view', module: 'admin' },
      { key: 'roles', label: 'Roles', icon: 'admin_panel_settings', path: '/app/roles', permissionCode: 'roles.view', module: 'admin' },
      { key: 'tenant-catalogs', label: 'Catalogos', icon: 'list_alt', path: '/app/tenant-catalogs', permissionCode: 'tenant_catalogs.view', module: 'admin' },
      { key: 'tenant-business-rules', label: 'Reglas de negocio', icon: 'rule', path: '/app/tenant-business-rules', permissionCode: 'tenant_business_rules.view', module: 'admin' },
      { key: 'tenant-settings', label: 'Ajustes', icon: 'tune', path: '/app/tenant-settings', permissionCode: 'tenant_settings.view', module: 'admin' },
      { key: 'sequence-settings', label: 'Numeración de códigos', icon: 'pin', path: '/app/sequence-settings', permissionCode: 'sequence_settings.view', module: 'admin' },
      { key: 'audit', label: 'Auditoria', icon: 'history', path: '/app/audit-logs', permissionCode: 'platform.audit_logs.view', module: 'admin' },
    ],
  },
  {
    module: 'platform',
    title: 'Permisos de Plataforma',
    items: [
      { key: 'tenants', label: 'Tenants', icon: 'apartment', path: '/app/tenants', permissionCode: 'platform.tenants.view', module: 'platform' },
      { key: 'platform-identities', label: 'Usuarios globales', icon: 'badge', path: '/app/platform-identities', permissionCode: 'platform.identities.view', module: 'platform' },
      { key: 'platform-roles', label: 'Roles de plataforma', icon: 'admin_panel_settings', path: '/app/platform-roles', permissionCode: 'platform.roles.view', module: 'platform' },
      { key: 'platform-permissions', label: 'Permisos de plataforma', icon: 'key', path: '/app/platform-permissions', permissionCode: 'platform.permissions.view', module: 'platform' },

      { key: 'general-catalogs', label: 'Catalogos generales', icon: 'list_alt', path: '/app/general-catalogs', permissionCode: 'general_catalogs.view', module: 'platform' },
      { key: 'process-rules', label: 'Reglas de proceso', icon: 'rule', path: '/app/process-rules', permissionCode: 'process_rules.view', module: 'platform' },
      { key: 'business-rules', label: 'Reglas de negocio', icon: 'rule', path: '/app/business-rules', permissionCode: 'business_rules.view', module: 'platform' },
      { key: 'general-settings', label: 'Ajustes generales', icon: 'settings', path: '/app/general-settings', permissionCode: 'general_settings.view', module: 'platform' },
    ],
  },
];
